import { Check, ChevronRight, Maximize, User } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Layout } from "@/components/layout";

export function Rooms() {
  const [selectedVilla, setSelectedVilla] = useState<number | null>(null);

  const villaImages = [
    "/images/villas/villa1.jpg",
    "/images/villas/villa2.jpg",
    "/images/villas/villa3.jpg",
    "/images/villas/villa4.jpg",
    "/images/villas/villa5.jpg",
    "/images/villas/villa6.jpg",
    "/images/villas/villa7.jpg",
    "/images/villas/villa1.jpg", // Repeat first image for 8th villa
  ];

  const villas = Array.from({ length: 8 }, (_, i) => ({
    number: i + 1,
    name: `Beachfront Villa ${i + 1}`,
    image: villaImages[i],
    size: "85 sqm",
    maxGuests: 2,
    features: [
      "Direct beach access",
      "Private terrace with ocean view",
      "King-size bed with luxury linens",
      "Spacious ensuite bathroom",
      "Mini bar and coffee station",
      "Air conditioning & ceiling fan",
      "Smart TV with streaming",
      "Free high-speed WiFi",
    ],
  }));

  const amenities = [
    "Daily housekeeping",
    "24-hour room service",
    "Complimentary beach towels",
    "In-room safe",
    "Bathrobes and slippers",
    "Premium toiletries",
    "Hair dryer",
    "Umbrella",
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[400px]">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/images/rooms-hero.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        <div className="relative h-full flex items-center justify-center text-center px-6">
          <div className="max-w-3xl">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
              Beachfront Villas
            </h1>
            <p className="text-xl text-white/95 font-light tracking-wide">
              Eight exclusive sanctuaries where the ocean meets luxury
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 mb-6">
            Your Private Paradise
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-8" />
          <p className="text-lg text-stone-700 leading-relaxed">
            Each of our eight beachfront villas offers an intimate escape with unparalleled ocean
            views. Thoughtfully designed to blend seamlessly with the natural beauty of Samui, every
            villa provides direct beach access and a private terrace where you can watch the sunset
            paint the sky in brilliant hues.
          </p>
        </div>
      </section>

      {/* Villa Cards */}
      <section className="py-16 px-6 bg-stone-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {villas.map((villa) => (
              <div
                key={villa.number}
                className="bg-white border border-stone-200 overflow-hidden hover:shadow-xl transition-shadow"
              >
                <div className="relative h-64">
                  <img src={villa.image} alt={villa.name} className="w-full h-full object-cover" />
                  <div className="absolute top-4 left-4 bg-stone-900 text-white px-4 py-2 text-sm font-semibold tracking-wider">
                    VILLA {villa.number}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="font-heading text-2xl font-bold text-stone-900 mb-4">
                    {villa.name}
                  </h3>

                  <div className="flex gap-6 mb-6 text-sm text-stone-600">
                    <div className="flex items-center gap-2">
                      <Maximize size={16} />
                      <span>{villa.size}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      <span>Up to {villa.maxGuests} guests</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-6">
                    {villa.features.slice(0, 4).map((feature, index) => (
                      <div key={index} className="flex items-start gap-2 text-sm text-stone-700">
                        <Check size={16} className="text-luxury-gold flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() =>
                      setSelectedVilla(selectedVilla === villa.number ? null : villa.number)
                    }
                    className="text-stone-900 font-semibold text-sm hover:text-stone-600 transition-colors mb-4 flex items-center gap-1"
                  >
                    {selectedVilla === villa.number ? "Show less" : "View all features"}
                    <ChevronRight
                      size={16}
                      className={`transition-transform ${selectedVilla === villa.number ? "rotate-90" : ""}`}
                    />
                  </button>

                  {selectedVilla === villa.number && (
                    <div className="grid grid-cols-2 gap-3 mb-6 pt-4 border-t border-stone-200">
                      {villa.features.slice(4).map((feature, index) => (
                        <div key={index} className="flex items-start gap-2 text-sm text-stone-700">
                          <Check size={16} className="text-luxury-gold flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <Link
                    to={`/book?villa=${villa.number}`}
                    className="block w-full bg-stone-800 text-white px-6 py-3 text-center text-xs tracking-widest uppercase font-semibold hover:bg-stone-700 transition-colors"
                  >
                    Book This Villa
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Amenities Section */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-stone-900 text-center mb-4">
            Included Amenities
          </h2>
          <div className="w-24 h-px bg-luxury-gold mx-auto mb-12" />

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {amenities.map((amenity, index) => (
              <div key={index} className="flex items-start gap-3">
                <Check size={20} className="text-luxury-gold flex-shrink-0 mt-0.5" />
                <span className="text-stone-700">{amenity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Reserve Your Villa?
          </h2>
          <p className="text-lg text-stone-300 mb-8 leading-relaxed">
            Limited availability. Contact us today to secure your beachfront sanctuary.
          </p>
          <Link
            to="/book"
            className="bg-white text-stone-900 px-10 py-4 text-sm tracking-widest uppercase font-semibold hover:bg-stone-100 transition-colors inline-block"
          >
            Make a Reservation
          </Link>
        </div>
      </section>
    </Layout>
  );
}
